/**
 * DI-04 — "Show what's on the current slide".
 *
 * Fully local: no AI call, no write, no confirm (PowerPoint spec §6). Because
 * it never needs the model it must keep working when the AI provider is
 * disconnected (ST-03, DI-04 AC-5), so the chat hook checks it before the
 * AI-offline guard.
 *
 *   isSlideContentRequest  — does this prompt ask for DI-04?
 *   formatSlideContentReply — turn a read result into the chat bubble (markdown)
 *   answerSlideContentRequest — read + format + telemetry, never throws
 *   buildSlideShapesToolResult — the same read as data, for the AI fallback (AIVA-8913)
 */

import { logOfficeError, startCapability, type CapabilityResult, type CapabilityTrigger } from "../../../telemetry";
import { readCurrentSlideShapes, type SlideShapeInfo, type SlideShapesReadResult } from "../services/slideShapes";

export const SLIDE_CONTENT_CAPABILITY_ID = "DI-04";

/** Preview length before the ellipsis (DI-04 AC-2). */
export const SHAPE_PREVIEW_MAX_CHARS = 200;

export const SLIDE_EMPTY_MESSAGE = "This slide is empty.";
export const SLIDE_UNREADABLE_MESSAGE =
  "AiVA can't read the shapes on this slide in this version of PowerPoint.";
/** Not in the spec: shown in Slide Sorter view or with nothing selected. */
export const NO_SLIDE_SELECTED_MESSAGE = "Select a slide in PowerPoint, then ask again.";

/**
 * G-8 approved shape-type map, keyed on the lower-cased Office.js value (the
 * enum is PascalCase — `"TextBox"` — while the spec writes `textBox`).
 * `diagram` and `graphic` are newer enum members the spec predates; they take
 * the labels of their nearest spec entry (SmartArt → Diagram, image → Picture).
 */
const SHAPE_TYPE_LABELS: Readonly<Record<string, string>> = {
  textbox: "Text box",
  placeholder: "Placeholder",
  geometricshape: "Shape",
  image: "Picture",
  graphic: "Picture",
  table: "Table",
  chart: "Chart",
  smartart: "Diagram",
  diagram: "Diagram",
  group: "Grouped shapes",
  line: "Line",
  connector: "Line",
  media: "Media",
  video: "Media",
  audio: "Media",
};

/** Anything unmapped reads "Shape" — never show a raw enum (G-8). */
export function labelForShapeType(type: string | null | undefined): string {
  return SHAPE_TYPE_LABELS[(type ?? "").toLowerCase()] ?? "Shape";
}

/**
 * Accepted phrasings from the spec, matched against the WHOLE prompt (bar a
 * polite prefix and trailing punctuation). Anchoring matters: a local handler
 * short-circuits the AI, so "tighten what's on this slide" must fall through
 * to SC-06 rather than be answered with a shape list.
 */
const POLITE_PREFIX = String.raw`(?:(?:please|pls|hey aiva|aiva)[,\s]+)?(?:(?:can|could|would) you\s+)?(?:please\s+)?`;
const TRAILER = String.raw`(?:[,\s]+please)?\s*[?.!]*\s*`;
// A bare "current slide" counts too: "what is on current slide" went to the AI
// and got a prose answer instead of the shape list.
const THIS_SLIDE = String.raw`(?:this|the current|the|my current|my|current)\s+slide`;

const SLIDE_CONTENT_PATTERNS: readonly RegExp[] = [
  // what's / whats on this slide · what is in the current slide · show me what's on this slide
  String.raw`(?:(?:show|tell)\s+(?:me\s+)?)?what(?:['’]?s|\s+is)\s+(?:on|in)\s+${THIS_SLIDE}`,
  // describe this slide · describe the current slide
  String.raw`describe\s+${THIS_SLIDE}`,
  // list the shapes on this slide · show the content of the current slide
  String.raw`(?:list|show)\s+(?:me\s+)?(?:all\s+)?(?:the\s+)?(?:shapes|content|contents)\s+(?:on|in|of)\s+${THIS_SLIDE}`,
].map((body) => new RegExp(String.raw`^\s*${POLITE_PREFIX}${body}${TRAILER}$`, "i"));

export function isSlideContentRequest(text: string): boolean {
  if (!text || text.trimStart().startsWith("/")) return false;
  return SLIDE_CONTENT_PATTERNS.some((re) => re.test(text));
}

/**
 * PowerPoint separates paragraphs with \r and soft line breaks with \v. Keep
 * each as its own line, so a placeholder holding a title and a subtitle
 * doesn't read as one run-on sentence. Blank lines are dropped and runs of
 * spaces inside a line are collapsed.
 */
function normaliseLines(text: string): string {
  return text
    .split(/\r\n|[\r\n\v\u2028\u2029]/)
    .map((line) => line.replace(/[^\S\r\n]+/g, " ").trim())
    .filter((line) => line.length > 0)
    .join("\n");
}

/**
 * Preview of a shape's text, one line per paragraph, capped at `max` code
 * points in total (line breaks count). Counting by code point means an emoji
 * is never split into a lone surrogate.
 */
export function toShapePreview(text: string, max: number = SHAPE_PREVIEW_MAX_CHARS): string {
  const chars = Array.from(normaliseLines(text));
  if (chars.length <= max) return chars.join("");
  return `${chars.slice(0, max).join("").trimEnd()}…`;
}

/**
 * Slide text is user content rendered through react-markdown: escape every
 * markdown-significant character so "**Q3**" or "# Agenda" in a shape shows
 * literally rather than as formatting.
 */
export function escapeMarkdown(text: string): string {
  return text.replace(/[\\`*_{}[\]()#+\-.!|<>~]/g, String.raw`\$&`);
}

export const NO_TEXT_PREVIEW = "_(no text)_";
/** For a shape that may hold text AiVA couldn't read (e.g. a group on an older host). */
export const TEXT_NOT_LISTED_PREVIEW = "_(text inside isn't listed)_";

function emptyPreviewFor(shape: SlideShapeInfo): string {
  return shape.textUnavailable ? TEXT_NOT_LISTED_PREVIEW : NO_TEXT_PREVIEW;
}

function shapeCountHeader(count: number): string {
  return `**${count}** ${count === 1 ? "shape" : "shapes"} on this slide:`;
}

export function formatSlideContentReply(result: SlideShapesReadResult): string {
  switch (result.status) {
    case "empty":
      return SLIDE_EMPTY_MESSAGE;
    case "no_slide":
      return NO_SLIDE_SELECTED_MESSAGE;
    case "unsupported":
      return SLIDE_UNREADABLE_MESSAGE;
    case "ok": {
      const entries = result.shapes.map((shape, i) => {
        const label = `**${i + 1}. ${labelForShapeType(shape.type)}**`;
        const preview = shape.hasText ? toShapePreview(shape.text) : "";
        // Trailing backslash = CommonMark hard line break: the label, then one
        // line per paragraph of the preview.
        const lines = preview ? preview.split("\n").map(escapeMarkdown) : [emptyPreviewFor(shape)];
        return [label, ...lines].join("\\\n");
      });
      return [shapeCountHeader(result.shapes.length), ...entries].join("\n\n");
    }
  }
}

/**
 * AIVA-8913 — what the `ppt_get_slide_shapes` tool returns when the DI-04
 * regex missed and the prompt reached `ppt_general`. The same G-8 labels,
 * previews and empty / unreadable copy as the local reply, as data rather than
 * markdown: the model writes its own reply from it.
 */
export function buildSlideShapesToolResult(result: SlideShapesReadResult): Record<string, unknown> {
  switch (result.status) {
    case "empty":
      return { readOk: true, shapeCount: 0, shapes: [], message: SLIDE_EMPTY_MESSAGE };
    case "no_slide":
      return { readOk: false, message: NO_SLIDE_SELECTED_MESSAGE };
    case "unsupported":
      return { readOk: false, message: SLIDE_UNREADABLE_MESSAGE };
    case "ok":
      return {
        readOk: true,
        shapeCount: result.shapes.length,
        shapes: result.shapes.map((shape, i) => ({
          number: i + 1,
          label: labelForShapeType(shape.type),
          text: shape.hasText ? toShapePreview(shape.text) : "",
          ...(shape.textUnavailable ? { textUnavailable: true } : {}),
        })),
      };
  }
}

function outcomeFor(result: SlideShapesReadResult): CapabilityResult {
  const base = { aiUsed: false, wroteToDocument: false } as const;
  switch (result.status) {
    case "ok":
      return { ...base, outcome: "success", shapeCount: result.shapes.length };
    case "empty":
      return { ...base, outcome: "success", shapeCount: 0 };
    case "no_slide":
      return { ...base, outcome: "no_match", errorCode: "no_slide_selected" };
    case "unsupported":
      return { ...base, outcome: "unsupported_api", errorCode: result.errorCode };
  }
}

export interface AnswerSlideContentOptions {
  trigger?: CapabilityTrigger;
  /** Injected for tests; defaults to the Office.js reader. */
  read?: () => Promise<SlideShapesReadResult>;
}

/**
 * Read the current slide and return the reply text. Never rejects: the reader
 * already reports host failures as `unsupported`, and anything unexpected
 * (a bug in the reader or formatter) is logged, closed out in telemetry as an
 * error, and shown to the user as the unreadable message rather than lost.
 */
export async function answerSlideContentRequest(options: AnswerSlideContentOptions = {}): Promise<string> {
  const { trigger = "natural_language", read = readCurrentSlideShapes } = options;
  const span = startCapability({
    capabilityId: SLIDE_CONTENT_CAPABILITY_ID,
    trigger,
    facts: { aiUsed: false, wroteToDocument: false },
  });
  try {
    const result = await read();
    const reply = formatSlideContentReply(result);
    span.complete(outcomeFor(result));
    return reply;
  } catch (err) {
    logOfficeError(err, "ppt-slide-content");
    span.complete({ outcome: "error", errorCode: "unexpected", aiUsed: false, wroteToDocument: false });
    return SLIDE_UNREADABLE_MESSAGE;
  }
}
